/**
 * The project's hiring requests that are waiting for confirmation: one person asked for
 * hiring to be raised, and a person who may raise hiring has not confirmed or declined it
 * yet. Nothing of a waiting request has been sent to Recruitment.
 *
 * ONE READ, SHARED. The tab strip (the count on the Hiring tab), the Stores tab (the line
 * under a store's Hiring pill, and whether "Ask for hiring" is offered for it) and the
 * Hiring tab all show the same list, so it is read once per person and project and kept
 * in one store with subscribers, as the access answer is. Each screen that opens asks
 * again (two that open together share the one call), and what is on show stays on show
 * while it is re-read.
 *
 * ASKED ONLY WHEN THE SERVER SAID TO. `enabled` is the access answer's `canSeeRequests`:
 * Raise is on for the project and on the server, and this person holds the Stores tick
 * or "Raise & cancel hiring". Without it nothing is asked and `status` is `off`: a person
 * with Hiring (view) alone, and every project with Raise off, never makes this call. What
 * they may do with a request (send, confirm, decline) is the list's own `can`.
 *
 * `status`:
 *   - `off`     hiring requests are not shown: not asked for, or the server refused the
 *               list (404 Raise not enabled, 403 not this person's). SILENTLY: no message
 *               is ever drawn for it, and the screens look as they did before.
 *   - `loading` the first answer for this person and project is on its way.
 *   - `ready`   `requests` holds every waiting request, newest first.
 *   - `failed`  the list could not be read, and there is no earlier one to show. Nothing
 *               is drawn then either: a count or an "Ask for hiring" control drawn on a
 *               list that was not read would be a guess.
 *
 * `can` is what the LIST says this person may do (send one, confirm one, decline one).
 * All three are false until it has answered, so no control is ever drawn on a guess.
 *
 * `apply(request)` puts one request the server has just answered with (sent, withdrawn,
 * declined, confirmed) into the list at once: in while it waits, out once it does not.
 * `reload()` reads the list again without clearing what is on show.
 *
 * When any call is refused because Raise was switched off, the screen that saw it calls
 * {@link forgetHiringRequests}: hiring requests go from every screen at once, with the
 * Raise controls. Lists are kept per PERSON and project, so someone who signs in after
 * another person in the same browser tab is never shown the first person's.
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { hiringLinksAPI, type HiringRequest, type HiringRequestList } from '../../services/api';
import { hiringStatusOf, isHiringOff } from './hiringText';
import { forgetHiringAccess } from './useHiringAccess';
import { storeHiringKey } from './useStoreHiring';

export const REQUESTS_PAGE_LIMIT = 500;

export type HiringRequestsStatus = 'off' | 'loading' | 'ready' | 'failed';

/** What this person may do with hiring requests here, as the list answered it. */
export interface HiringRequestsCan { send: boolean; confirm: boolean; decline: boolean }

export const NO_REQUEST_CAN: HiringRequestsCan = { send: false, confirm: false, decline: false };

/** What a list answer says this person may do. Anything but a plain `true` is a no. */
export const requestCanOf = (list: Pick<HiringRequestList, 'can'> | null | undefined): HiringRequestsCan => {
  const send = list?.can?.send === true;
  const confirm = list?.can?.confirm === true;
  const decline = list?.can?.decline === true;
  return send || confirm || decline ? { send, confirm, decline } : NO_REQUEST_CAN;
};

export interface HiringRequests {
  status: HiringRequestsStatus;
  /** How many of the project's hiring requests wait for confirmation. 0 unless `ready`. */
  waiting: number;
  /** Every waiting request, newest first. */
  requests: readonly HiringRequest[];
  /** The waiting request of each store, keyed by store id in lower case. A store has at most one. */
  byStore: ReadonlyMap<string, HiringRequest>;
  /** The waiting requests for manager roles. */
  roles: readonly HiringRequest[];
  can: HiringRequestsCan;
  reload: () => void;
  apply: (request: HiringRequest) => void;
}

interface Kept {
  status: Exclude<HiringRequestsStatus, 'off'> | 'refused';
  waiting: number;
  requests: readonly HiringRequest[];
  can: HiringRequestsCan;
}

const NO_REQUESTS: readonly HiringRequest[] = [];
const NO_STORES: ReadonlyMap<string, HiringRequest> = new Map();
const LOADING: Kept = { status: 'loading', waiting: 0, requests: NO_REQUESTS, can: NO_REQUEST_CAN };
const FAILED: Kept = { status: 'failed', waiting: 0, requests: NO_REQUESTS, can: NO_REQUEST_CAN };
const REFUSED: Kept = { status: 'refused', waiting: 0, requests: NO_REQUESTS, can: NO_REQUEST_CAN };

const kept = new Map<string, Kept>();
const inFlight = new Map<string, Promise<void>>();
// The latest read asked for per key: an answer to an earlier one is dropped.
const asked = new Map<string, number>();
const listeners = new Set<() => void>();

const keyOf = (projectId: number, userKey: string): string => `${userKey}|${projectId}`;

const notify = (): void => {
  for (const listener of [...listeners]) listener();
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const keep = (key: string, next: Kept): void => {
  kept.set(key, next);
  notify();
};

/** Every page of the project's waiting requests, or a throw. */
const readAll = async (projectId: number): Promise<{ requests: HiringRequest[]; waiting: number; can: HiringRequestsCan }> => {
  const requests: HiringRequest[] = [];
  const cursors = new Set<string>();
  let cursor: string | null = null;
  // Said by every page alike; the first page's word is kept.
  let first: { waiting: number; can: HiringRequestsCan } | null = null;
  for (;;) {
    const res = await hiringLinksAPI.requests(
      projectId,
      { status: 'waiting', limit: REQUESTS_PAGE_LIMIT, ...(cursor ? { cursor } : {}) },
      { background: true },
    );
    const data = res?.data?.data;
    if (!data || !Array.isArray(data.requests)) throw new Error('unreadable hiring request list');
    requests.push(...data.requests);
    first = first ?? { waiting: Number(data.waiting) || 0, can: requestCanOf(data) };
    cursor = data.nextCursor ?? null;
    if (!cursor) return { requests, ...first };
    // The same cursor twice would be the same page for ever; so would an empty page that names a next one.
    if (cursors.has(cursor) || data.requests.length === 0) throw new Error('hiring request list cursor did not move on');
    cursors.add(cursor);
  }
};

/** Reads the list for one person and project into the shared store. Never rejects. */
const read = (projectId: number, userKey: string, again = false): Promise<void> => {
  const key = keyOf(projectId, userKey);
  const existing = inFlight.get(key);
  // Two screens that open together share one call; a re-read asked for on purpose does not wait for it.
  if (existing && !again) return existing;

  const turn = (asked.get(key) ?? 0) + 1;
  asked.set(key, turn);
  // What is on show stays while it is read again.
  if (kept.get(key)?.status !== 'ready') keep(key, LOADING);

  // Inside the chain: a throw while building the request must never reach the page.
  const request: Promise<void> = Promise.resolve()
    .then(() => readAll(projectId))
    .then(
      ({ requests, waiting, can }) => {
        if (asked.get(key) !== turn) return;
        const stillWaiting = requests.filter((r) => r?.standing === 'waiting');
        // The count is the server's own, and never less than the rows that came with it.
        keep(key, { status: 'ready', requests: stillWaiting, waiting: Math.max(waiting, stillWaiting.length), can });
      },
      (err: unknown) => {
        if (asked.get(key) !== turn) return;
        const status = hiringStatusOf(err);
        // Hiring itself went off for the project: every hiring screen goes dark.
        if (isHiringOff(err)) forgetHiringAccess(projectId);
        // Raise is off, or hiring requests are not this person's: off, and nothing is said.
        if (status === 404 || status === 403) keep(key, REFUSED);
        // A re-read that failed: the list already on show stays.
        else if (kept.get(key)?.status !== 'ready') keep(key, FAILED);
      },
    )
    .finally(() => {
      if (inFlight.get(key) === request) inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
};

/** One request, as the server just answered it, put into a list: in while it waits, out once it does not. */
const withRequest = (was: Kept, request: HiringRequest): Kept => {
  const listed = was.requests.some((r) => r.id === request.id);
  const others = was.requests.filter((r) => r.id !== request.id);
  if (request.standing === 'waiting') {
    return { ...was, requests: [request, ...others], waiting: listed ? was.waiting : was.waiting + 1 };
  }
  return listed ? { ...was, requests: others, waiting: Math.max(0, was.waiting - 1) } : was;
};

export const useHiringRequests = (
  projectId: number | null | undefined,
  userKey: string,
  enabled: boolean,
): HiringRequests => {
  const key = enabled && projectId ? keyOf(projectId, userKey) : '';
  const state = useSyncExternalStore(subscribe, () => (key ? kept.get(key) ?? null : null));

  useEffect(() => {
    if (!enabled || !projectId) return;
    void read(projectId, userKey);
  }, [enabled, projectId, userKey]);

  const reload = useCallback(() => {
    if (enabled && projectId) void read(projectId, userKey, true);
  }, [enabled, projectId, userKey]);

  const apply = useCallback((request: HiringRequest) => {
    if (!enabled || !projectId || !request) return;
    const k = keyOf(projectId, userKey);
    const was = kept.get(k);
    // Only onto a list that was read: with none, one request would be the whole list.
    if (was?.status !== 'ready') return;
    const next = withRequest(was, request);
    if (next !== was) keep(k, next);
    // A list asked for before this answer is older than it: it is asked for again.
    if (inFlight.has(k)) void read(projectId, userKey, true);
  }, [enabled, projectId, userKey]);

  const ready = state?.status === 'ready' ? state : null;
  const requests = ready?.requests ?? NO_REQUESTS;
  const byStore = useMemo(() => {
    if (requests.length === 0) return NO_STORES;
    const map = new Map<string, HiringRequest>();
    for (const request of requests) {
      const storeId = request.subject?.kind === 'store' ? request.subject.storeId : null;
      // Newest first, so the first one seen for a store is the one that stays.
      if (storeId && !map.has(storeHiringKey(storeId))) map.set(storeHiringKey(storeId), request);
    }
    return map as ReadonlyMap<string, HiringRequest>;
  }, [requests]);
  const roles = useMemo(() => requests.filter((request) => request.subject?.kind === 'role'), [requests]);

  const status: HiringRequestsStatus = !key
    ? 'off'
    : !state
      ? 'loading'
      : state.status === 'refused' ? 'off' : state.status;

  return {
    status,
    waiting: ready?.waiting ?? 0,
    requests,
    byStore,
    roles,
    can: ready?.can ?? NO_REQUEST_CAN,
    reload,
    apply,
  };
};

/**
 * A hiring call was just refused because Raise is switched off for this project, or on
 * the server: hiring requests exist only where Raise is on, so they go from every screen
 * showing this project, and nothing is said. An answer still on its way is dropped. The
 * next screen to open asks the server again.
 */
export const forgetHiringRequests = (projectId: number | null | undefined): void => {
  if (!projectId) return;
  const suffix = `|${projectId}`;
  for (const key of new Set([...kept.keys(), ...inFlight.keys()])) {
    if (!key.endsWith(suffix)) continue;
    asked.set(key, (asked.get(key) ?? 0) + 1);
    kept.set(key, REFUSED);
  }
  notify();
};

/** Drops every kept list, so the screens on show ask again. In tests, and when the person signs out. */
export const clearHiringRequestsCache = (): void => {
  kept.clear();
  inFlight.clear();
  asked.clear();
  notify();
};
